/**
 * A QR encoder: byte mode, error-correction level M, versions 1–12.
 *
 * Written rather than depended on, for the same reason the paper grain is
 * inlined — an invite that will not scan is worse than no invite, and a QR
 * library is a lot of surface for one screen. Correctness is not asserted on
 * faith: `qr.test.ts` compares this implementation's module matrix against a
 * reference encoder for a spread of inputs and versions, so a mistake in any of
 * the tables below fails the build rather than someone's phone.
 *
 * Level M (≈15% recovery) is chosen over L because these codes get pointed at
 * from across a room, often on a screen at an angle.
 */

/* ------------------------------------------------------------ GF(256) maths */

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
{
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d; // primitive polynomial
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255]!;
}

const mul = (a: number, b: number): number =>
  a === 0 || b === 0 ? 0 : EXP[LOG[a]! + LOG[b]!]!;

/** Reed-Solomon generator polynomial of the given degree. */
function generator(degree: number): Uint8Array {
  let poly = new Uint8Array([1]);
  for (let i = 0; i < degree; i++) {
    const next = new Uint8Array(poly.length + 1);
    for (let j = 0; j < poly.length; j++) {
      next[j] = (next[j] ?? 0) ^ mul(poly[j]!, 1);
      next[j + 1] = (next[j + 1] ?? 0) ^ mul(poly[j]!, EXP[i]!);
    }
    poly = next;
  }
  return poly;
}

/** The error-correction codewords for one block. */
function remainder(data: Uint8Array, ecLength: number): Uint8Array {
  const gen = generator(ecLength);
  const buffer = new Uint8Array(data.length + ecLength);
  buffer.set(data);
  for (let i = 0; i < data.length; i++) {
    const factor = buffer[i]!;
    if (factor === 0) continue;
    for (let j = 0; j < gen.length; j++) {
      buffer[i + j] = (buffer[i + j] ?? 0) ^ mul(gen[j]!, factor);
    }
  }
  return buffer.slice(data.length);
}

/* ------------------------------------------------------------------ tables */

/**
 * Per version at level M: EC codewords per block, then the two block groups as
 * `[count, dataCodewords]`. The test cross-checks every row against the
 * standard's total-codeword count for that version.
 */
const BLOCKS_M: Record<number, [number, [number, number], [number, number]]> = {
  1: [10, [1, 16], [0, 0]],
  2: [16, [1, 28], [0, 0]],
  3: [26, [1, 44], [0, 0]],
  4: [18, [2, 32], [0, 0]],
  5: [24, [2, 43], [0, 0]],
  6: [16, [4, 27], [0, 0]],
  7: [18, [4, 31], [0, 0]],
  8: [22, [2, 38], [2, 39]],
  9: [22, [3, 36], [2, 37]],
  10: [26, [4, 43], [1, 44]],
  11: [30, [1, 50], [4, 51]],
  12: [22, [6, 36], [2, 37]],
};

/** Alignment-pattern centre coordinates per version. */
const ALIGNMENT: Record<number, number[]> = {
  1: [],
  2: [6, 18],
  3: [6, 22],
  4: [6, 26],
  5: [6, 30],
  6: [6, 34],
  7: [6, 22, 38],
  8: [6, 24, 42],
  9: [6, 26, 46],
  10: [6, 28, 50],
  11: [6, 30, 54],
  12: [6, 32, 58],
};

/** 18-bit version information, versions 7 and up. */
const VERSION_INFO: Record<number, number> = {
  7: 0x07c94,
  8: 0x085bc,
  9: 0x09a99,
  10: 0x0a4d3,
  11: 0x0bbf6,
  12: 0x0c762,
};

/** 15-bit format information for level M, indexed by mask. */
const FORMAT_M = [0x5412, 0x5125, 0x5e7c, 0x5b4b, 0x45f9, 0x40ce, 0x4f97, 0x4aa0];

const MASKS: ((row: number, col: number) => boolean)[] = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (_r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

/* ---------------------------------------------------------------- encoding */

function dataCapacity(version: number): number {
  const [, g1, g2] = BLOCKS_M[version]!;
  return g1[0] * g1[1] + g2[0] * g2[1];
}

function countBits(version: number): number {
  return version <= 9 ? 8 : 16;
}

function chooseVersion(byteLength: number): number {
  for (let v = 1; v <= 12; v++) {
    const headerBits = 4 + countBits(v);
    if (headerBits + byteLength * 8 <= dataCapacity(v) * 8) return v;
  }
  throw new Error('too much data for a version-12 QR code');
}

class BitBuffer {
  private bits: number[] = [];
  put(value: number, length: number): void {
    for (let i = length - 1; i >= 0; i--) this.bits.push((value >>> i) & 1);
  }
  get length(): number {
    return this.bits.length;
  }
  toCodewords(count: number): Uint8Array {
    const out = new Uint8Array(count);
    for (let i = 0; i < this.bits.length; i++) {
      if (this.bits[i]) out[i >> 3]! |= 0x80 >> (i & 7);
    }
    return out;
  }
}

/** Data codewords, padded, split into blocks, and interleaved with their EC. */
function codewords(text: string, version: number): Uint8Array {
  const bytes = new TextEncoder().encode(text);
  const capacity = dataCapacity(version);

  const buffer = new BitBuffer();
  buffer.put(0b0100, 4); // byte mode
  buffer.put(bytes.length, countBits(version));
  for (const byte of bytes) buffer.put(byte, 8);
  // Terminator, then pad to a whole codeword, then the standard pad bytes.
  buffer.put(0, Math.min(4, capacity * 8 - buffer.length));
  if (buffer.length % 8 !== 0) buffer.put(0, 8 - (buffer.length % 8));
  const data = buffer.toCodewords(capacity);
  for (let i = Math.ceil(buffer.length / 8), toggle = true; i < capacity; i++, toggle = !toggle) {
    data[i] = toggle ? 0xec : 0x11;
  }

  const [ecLength, g1, g2] = BLOCKS_M[version]!;
  const blocks: { data: Uint8Array; ec: Uint8Array }[] = [];
  let offset = 0;
  for (const [count, size] of [g1, g2]) {
    for (let b = 0; b < count; b++) {
      const slice = data.slice(offset, offset + size);
      offset += size;
      blocks.push({ data: slice, ec: remainder(slice, ecLength) });
    }
  }

  const out: number[] = [];
  const maxData = Math.max(...blocks.map((b) => b.data.length));
  for (let i = 0; i < maxData; i++) {
    for (const block of blocks) if (i < block.data.length) out.push(block.data[i]!);
  }
  for (let i = 0; i < ecLength; i++) {
    for (const block of blocks) out.push(block.ec[i]!);
  }
  return new Uint8Array(out);
}

/* ------------------------------------------------------------------ matrix */

type Grid = (boolean | null)[][];

function blank(size: number): Grid {
  return Array.from({ length: size }, () => Array.from({ length: size }, () => null));
}

function placeFinder(grid: Grid, row: number, col: number): void {
  for (let r = -1; r <= 7; r++) {
    for (let c = -1; c <= 7; c++) {
      const y = row + r;
      const x = col + c;
      if (y < 0 || y >= grid.length || x < 0 || x >= grid.length) continue;
      const onRing = (r >= 0 && r <= 6 && (c === 0 || c === 6)) ||
        (c >= 0 && c <= 6 && (r === 0 || r === 6));
      const inCore = r >= 2 && r <= 4 && c >= 2 && c <= 4;
      grid[y]![x] = onRing || inCore;
    }
  }
}

function reserveFormat(grid: Grid, version: number): void {
  const size = grid.length;
  for (let i = 0; i < 9; i++) {
    if (grid[8]![i] === null) grid[8]![i] = false;
    if (grid[i]![8] === null) grid[i]![8] = false;
  }
  for (let i = 0; i < 8; i++) {
    grid[8]![size - 1 - i] = grid[8]![size - 1 - i] ?? false;
    grid[size - 1 - i]![8] = grid[size - 1 - i]![8] ?? false;
  }
  grid[size - 8]![8] = true; // the always-dark module
  if (version >= 7) {
    for (let i = 0; i < 18; i++) {
      const a = Math.floor(i / 3);
      const b = (i % 3) + size - 11;
      grid[b]![a] = grid[b]![a] ?? false;
      grid[a]![b] = grid[a]![b] ?? false;
    }
  }
}

function skeleton(version: number): Grid {
  const size = version * 4 + 17;
  const grid = blank(size);

  placeFinder(grid, 0, 0);
  placeFinder(grid, 0, size - 7);
  placeFinder(grid, size - 7, 0);

  for (let i = 8; i < size - 8; i++) {
    grid[6]![i] = i % 2 === 0;
    grid[i]![6] = i % 2 === 0;
  }

  const centres = ALIGNMENT[version]!;
  const last = centres[centres.length - 1];
  for (const r of centres) {
    for (const c of centres) {
      // The three centres that would collide with a finder are omitted — and
      // only those. An alignment pattern that crosses the timing line is drawn
      // over it; skipping those shifts every data module after it.
      const onFinder =
        (r === 6 && c === 6) || (r === 6 && c === last) || (r === last && c === 6);
      if (onFinder) continue;
      for (let dr = -2; dr <= 2; dr++) {
        for (let dc = -2; dc <= 2; dc++) {
          grid[r + dr]![c + dc] = Math.max(Math.abs(dr), Math.abs(dc)) !== 1;
        }
      }
    }
  }

  reserveFormat(grid, version);
  return grid;
}

/** Zigzag placement, two columns at a time, skipping the vertical timing line. */
function placeData(grid: Grid, data: Uint8Array): boolean[][] {
  const size = grid.length;
  const reserved = grid.map((row) => row.map((cell) => cell !== null));
  const out = grid.map((row) => row.map((cell) => cell === true));

  let bit = 0;
  let upward = true;
  for (let right = size - 1; right > 0; right -= 2) {
    if (right === 6) right = 5; // the timing column is not a data column
    for (let step = 0; step < size; step++) {
      const row = upward ? size - 1 - step : step;
      for (const col of [right, right - 1]) {
        if (reserved[row]![col]) continue;
        const byte = data[bit >> 3] ?? 0;
        out[row]![col] = ((byte >> (7 - (bit & 7))) & 1) === 1;
        bit++;
      }
    }
    upward = !upward;
  }
  return out;
}

function applyMask(modules: boolean[][], reserved: boolean[][], mask: number): boolean[][] {
  const fn = MASKS[mask]!;
  return modules.map((row, r) =>
    row.map((cell, c) => (reserved[r]![c] ? cell : cell !== fn(r, c))),
  );
}

/**
 * Format information is written most-significant bit first: bit 14 lands at
 * (8, 0), not bit 0. Getting this backwards produces a code that is perfectly
 * well-formed and completely unreadable, which is exactly the kind of mistake
 * the reference comparison in `qr.test.ts` exists to catch.
 */
function writeFormat(modules: boolean[][], mask: number): void {
  const size = modules.length;
  const bits = FORMAT_M[mask]!;
  for (let i = 0; i < 15; i++) {
    const on = ((bits >> (14 - i)) & 1) === 1;

    // Copy one, around the top-left finder.
    if (i < 6) modules[8]![i] = on;
    else if (i < 8) modules[8]![i + 1] = on;
    else if (i === 8) modules[7]![8] = on;
    else modules[14 - i]![8] = on;

    // Copy two: the high bits run up from the bottom-left finder, the low bits
    // run along to the top-right. (size-8, 8) is the always-dark module and
    // belongs to neither.
    if (i < 7) modules[size - 1 - i]![8] = on;
    else modules[8]![size - 15 + i] = on;
  }
  modules[size - 8]![8] = true;
}

function writeVersion(modules: boolean[][], version: number): void {
  if (version < 7) return;
  const size = modules.length;
  const bits = VERSION_INFO[version]!;
  for (let i = 0; i < 18; i++) {
    const on = ((bits >> i) & 1) === 1;
    const a = Math.floor(i / 3);
    const b = (i % 3) + size - 11;
    modules[b]![a] = on;
    modules[a]![b] = on;
  }
}

/** The standard's four penalty rules, used to pick the least noisy mask. */
function penalty(modules: boolean[][]): number {
  const size = modules.length;
  let score = 0;

  const run = (get: (i: number, j: number) => boolean) => {
    for (let i = 0; i < size; i++) {
      let length = 1;
      for (let j = 1; j < size; j++) {
        if (get(i, j) === get(i, j - 1)) {
          length++;
        } else {
          if (length >= 5) score += length - 2;
          length = 1;
        }
      }
      if (length >= 5) score += length - 2;
    }
  };
  run((i, j) => modules[i]![j]!);
  run((i, j) => modules[j]![i]!);

  for (let r = 0; r < size - 1; r++) {
    for (let c = 0; c < size - 1; c++) {
      const v = modules[r]![c];
      if (v === modules[r]![c + 1] && v === modules[r + 1]![c] && v === modules[r + 1]![c + 1]) {
        score += 3;
      }
    }
  }

  const pattern = [true, false, true, true, true, false, true, false, false, false, false];
  const reversed = [...pattern].reverse();
  const matches = (get: (k: number) => boolean, at: number, p: boolean[]) =>
    p.every((want, k) => get(at + k) === want);
  for (let i = 0; i < size; i++) {
    for (let j = 0; j + 11 <= size; j++) {
      if (matches((k) => modules[i]![k]!, j, pattern)) score += 40;
      if (matches((k) => modules[i]![k]!, j, reversed)) score += 40;
      if (matches((k) => modules[k]![i]!, j, pattern)) score += 40;
      if (matches((k) => modules[k]![i]!, j, reversed)) score += 40;
    }
  }

  let dark = 0;
  for (const row of modules) for (const cell of row) if (cell) dark++;
  const ratio = (dark * 100) / (size * size);
  score += Math.floor(Math.abs(ratio - 50) / 5) * 10;
  return score;
}

export interface QrCode {
  size: number;
  version: number;
  /** `modules[row][col]` — true is a dark module. */
  modules: boolean[][];
}

export interface EncodeOptions {
  /**
   * Force a mask instead of choosing the least noisy one. Mask selection is an
   * optimisation, not a conformance requirement — two correct encoders may pick
   * differently — so the test suite pins it to compare matrices exactly.
   */
  mask?: number;
}

export function encodeQr(text: string, options: EncodeOptions = {}): QrCode {
  const version = chooseVersion(new TextEncoder().encode(text).length);
  const grid = skeleton(version);
  const reserved = grid.map((row) => row.map((cell) => cell !== null));
  const placed = placeData(grid, codewords(text, version));

  const build = (mask: number): boolean[][] => {
    const candidate = applyMask(placed, reserved, mask);
    writeFormat(candidate, mask);
    writeVersion(candidate, version);
    return candidate;
  };

  if (options.mask !== undefined) {
    return { size: grid.length, version, modules: build(options.mask) };
  }

  let best: boolean[][] | null = null;
  let bestScore = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    const candidate = build(mask);
    const score = penalty(candidate);
    if (score < bestScore) {
      bestScore = score;
      best = candidate;
    }
  }
  return { size: grid.length, version, modules: best! };
}

/** Reads the mask index back out of a finished code's format information. */
export function maskOf(modules: boolean[][]): number {
  let bits = 0;
  for (let i = 0; i < 15; i++) {
    let on: boolean;
    if (i < 6) on = modules[8]![i]!;
    else if (i < 8) on = modules[8]![i + 1]!;
    else if (i === 8) on = modules[7]![8]!;
    else on = modules[14 - i]![8]!;
    if (on) bits |= 1 << (14 - i);
  }
  const index = FORMAT_M.indexOf(bits);
  if (index < 0) throw new Error('format information is not level M');
  return index;
}

/** An SVG path covering every dark module — one element, no per-pixel nodes. */
export function qrPath(code: QrCode): string {
  const parts: string[] = [];
  for (let r = 0; r < code.size; r++) {
    for (let c = 0; c < code.size; c++) {
      if (code.modules[r]![c]) parts.push(`M${c} ${r}h1v1h-1z`);
    }
  }
  return parts.join('');
}
